import { TimeOption } from 'design-system-web/components/DatePicker/helpers';

export type { TimeOption };

const MINUTES_IN_DAY = 1440;
const SLOT_MINUTES = 30;
const START_SLOT_COUNT = MINUTES_IN_DAY / SLOT_MINUTES;
const END_SLOT_COUNT = START_SLOT_COUNT + 1;

function isLaterDay(time: Date, dayStart: Date): boolean {
  return (
    time.getFullYear() !== dayStart.getFullYear() ||
    time.getMonth() !== dayStart.getMonth() ||
    time.getDate() !== dayStart.getDate()
  );
}

// `dayStartIso` is the block's own start time and fixes the day the result is
// measured against, so an end time landing on the next day is the 12:00 AM
// end-of-day slot - 1440 - rather than 0.
export function minutesOfDay(iso: string, dayStartIso: string = iso): number {
  const time = new Date(iso);

  if (isLaterDay(time, new Date(dayStartIso))) return MINUTES_IN_DAY;

  return time.getHours() * 60 + time.getMinutes();
}

// 12:00 AM is only ever offered as an end time for the slot that closes the day.
function endMinutesOfDay(iso: string): number {
  const minutes = minutesOfDay(iso);

  return minutes === 0 ? MINUTES_IN_DAY : minutes;
}

export function minutesToTimeIso(minutesPastMidnight: number): string {
  const day = new Date();

  day.setHours(0, 0, 0, 0);
  day.setMinutes(minutesPastMidnight);

  return day.toISOString();
}

function buildSlots(count: number): TimeOption[] {
  const day = new Date();

  day.setHours(0, 0, 0, 0);

  return Array.from({ length: count }, (_, index) => {
    const date = new Date(day);

    date.setMinutes(index * SLOT_MINUTES);

    return { date };
  });
}

export function buildStartTimes(endTime: string | null): TimeOption[] {
  const options = buildSlots(START_SLOT_COUNT);

  if (!endTime) return options;

  const endMinute = endMinutesOfDay(endTime);

  return options.map((option, index) => ({
    ...option,
    disabled: index * SLOT_MINUTES >= endMinute,
  }));
}

// Slots at or before the selected start time can never produce a valid
// window, so they're dropped from the list entirely rather than kept around
// disabled - there's nothing useful for the person to do with them.
export function buildEndTimes(startTime: string | null): TimeOption[] {
  const options = buildSlots(END_SLOT_COUNT);

  if (!startTime) return options;

  const startMinute = minutesOfDay(startTime);

  return options.filter((_option, index) => index * SLOT_MINUTES > startMinute);
}
